import { createPortal } from "react-dom";
import React, { useEffect, useRef, useState } from "react";
import { SLOT_META } from "../components/shop/ShopUI";
import { ClassIcon } from "../components/ClassIcon";
import { useNavigate, useParams } from "react-router-dom";
import api, { wrestlerAvatar, ASSETS } from "../lib/api";
import { hotDogRain, rainCount } from "../lib/hotdogRain";
import { ClaimButton } from "../components/roster/RosterUI";
import WrestlerPortrait from "../components/WrestlerPortrait";
import AvatarHintTooltip from "../components/AvatarHintTooltip";
import { useAuth } from "../lib/auth";
import { RickyTutorial } from "../components/RickyTutorial";
import { toast } from "sonner";
import { Dumbbell, Swords, Megaphone, ArrowLeft, X, Zap, Skull, ArrowUp, Sparkles, Flame, Globe2, Crown, Pencil, Trophy, Landmark, Users, Briefcase, Dog, BarChart3, BicepsFlexed, Gauge, Clover, Shirt, UtensilsCrossed, PlusCircle } from "lucide-react";
import { STAT_META, bonusText as fmtBonus } from "../components/vault/VaultUI";
import { GradCard, SectionHeading, StatBlock, ActionTile, AccompItem, CountUp, LevelUpSpotlight, HintTip } from "../components/wrestler/WrestlerUI";
import { Flag, TOUR_LABELS, fmtFans } from "../lib/tour";

function xpForLevel(lvl) {
  return 100 + (lvl - 1) * 40 + (lvl - 1) ** 2 * 18;
}

// Which title divisions a wrestler can actually WIN (based on class).
// Heavyweight → HW only, Middleweight → MW only, Cruiserweight → CW only,
// Tag Team → TAG only, Wildcard → HW/MW/CW (never TAG).
function eligibleTitleDivisions(classId) {
  if (classId === "heavyweight" || classId === "middleweight" || classId === "cruiserweight") return [classId];
  if (classId === "tag_team" || classId === "tag_team_1") return ["tag_team"];
  if (classId === "wildcard") return ["heavyweight", "middleweight", "cruiserweight"];
  return [];
}
const DIVISION_LABEL_SHORT = { heavyweight: "HW", middleweight: "MW", cruiserweight: "CW", tag_team: "TAG" };
const DIVISION_LABEL_FULL = { heavyweight: "HEAVYWEIGHT", middleweight: "MIDDLEWEIGHT", cruiserweight: "CRUISERWEIGHT", tag_team: "TAG TEAM" };
const DIVISION_COLOR = { heavyweight: "#FFD700", middleweight: "#FFD700", cruiserweight: "#FFD700", tag_team: "#39FF14" };

function ItemTooltip({ item, anchor }) {
  if (!item || !anchor) return null;
  const left = Math.min(window.innerWidth - 80, Math.max(80, anchor.left + anchor.width / 2));
  return createPortal(
    <div data-testid="item-tooltip" className="fixed z-[200] pointer-events-none -translate-x-1/2 -translate-y-full bg-[#0A051A] border border-[#00F0FF] rounded-lg p-2 min-w-[140px] shadow-lg"
         style={{ left, top: anchor.top - 8 }}>
      <div className="font-heading text-xs neon-cyan mb-1">{item.name}</div>
      <div className="text-xs font-mono">
        {Object.entries(item.bonus || {}).map(([k, v]) => (
          <div key={k} className="text-[#39FF14]">{fmtBonus(k, v)}</div>
        ))}
      </div>
    </div>,
    document.body
  );
}

function EmptySlotIcon({ slot, label }) {
  const m = SLOT_META[slot] || SLOT_META.all;
  const Icon = m.icon;
  return (
    <div className="w-full h-full flex flex-col items-center justify-center gap-1" data-testid={`gear-slot-empty-icon-${slot}`}>
      <Icon size={30} strokeWidth={2.2} className="opacity-60 transition-[opacity,transform] duration-200 group-hover:opacity-100 group-hover:scale-110"
            style={{ color: m.color, filter: `drop-shadow(0 0 6px ${m.color}88)` }} />
      <div className="uppercase tracking-widest text-[10px]" style={{ color: m.color }}>{label}</div>
      <div className="text-[#695D8E] text-[9px]">Empty</div>
    </div>
  );
}

function GearSlot({ slot, label, itemId, items, onUnequip, onClick, detail }) {
  const shopItem = items.find(i => i.id === itemId);
  const item = detail ? { ...(shopItem || {}), id: itemId, name: detail.name, image: detail.image || shopItem?.image, bonus: detail.bonus } : (shopItem || null);
  const level = detail?.level;
  const imgSrc = item ? (item.image || `/items/${itemId}.webp`) : null;
  const [anchor, setAnchor] = useState(null);
  return (
    <div
      data-testid={`gear-slot-${slot}`}
      onClick={onClick}
      onMouseEnter={(e) => setAnchor(e.currentTarget.getBoundingClientRect())}
      onMouseLeave={() => setAnchor(null)}
      className="relative aspect-square cursor-pointer group"
    >
     <div className={`absolute inset-0 rounded-lg overflow-hidden ${item ? "slot-filled" : "slot-empty"}`}>
      {item ? (
        <>
          {imgSrc && (
            <img
              src={imgSrc}
              alt={item.name}
              className="absolute inset-0 w-full h-full object-cover"
              onError={(e) => { e.currentTarget.style.display = "none"; }}
            />
          )}
          <div className="absolute inset-0 bg-gradient-to-t from-[#0A051A]/95 via-[#0A051A]/25 to-transparent" />
          <div className="absolute bottom-1 left-0 right-0 px-1 text-center">
            <div className="font-heading text-xs text-white truncate leading-tight" style={{ textShadow: "0 1px 3px #000" }}>{item.name}</div>
            <div className="text-[9px] text-[#A89BCC] uppercase tracking-widest">{label}</div>
          </div>
          {level && level > 0 && (
            <div
              data-testid={`gear-level-${slot}`}
              className="absolute top-1 left-1 px-1 py-0.5 rounded text-[9px] font-heading font-bold"
              style={{
                background: "#0A051A",
                color: level >= 3 ? "#FFD700" : level >= 2 ? "#39FF14" : "#00F0FF",
                border: `1px solid ${level >= 3 ? "#FFD700" : level >= 2 ? "#39FF14" : "#00F0FF"}88`,
                boxShadow: level >= 2 ? `0 0 6px ${level >= 3 ? "#FFD700" : "#39FF14"}66` : "none",
              }}
              title={`Level ${level} · ${detail.copies} copies${detail.next_at ? ` · next at ${detail.next_at}` : ""}`}
            >
              L{level}
            </div>
          )}
          <button
            onClick={(e) => { e.stopPropagation(); onUnequip(); }}
            className="absolute top-1 right-1 p-1 rounded-full bg-[#FF007F]/60 hover:bg-[#FF007F] opacity-0 group-hover:opacity-100 transition-opacity"
            data-testid={`unequip-${slot}`}
          >
            <X size={10} />
          </button>
        </>
      ) : (
        <EmptySlotIcon slot={slot} label={label} />
      )}
     </div>
      {item && <ItemTooltip anchor={anchor} item={detail ? { name: item.name, bonus: detail.bonus } : item} />}
    </div>
  );
}

const EQUIP_BONUS_META = [
  ["power", "Power", "#FF007F", BicepsFlexed, ""],
  ["workrate", "Workrate", "#00F0FF", Gauge, ""],
  ["fortune", "Fortune", "#FFD700", Clover, ""],
  ["pop", "Pop", "#B44DFF", Megaphone, ""],
  ["xp_bonus", "XP Bonus", "#39FF14", Sparkles, "%"],
  ...["power_pct", "workrate_pct", "fortune_pct", "pop_pct", "passive_gains_pct", "training_hotdog_pct", "tour_fans_pct", "pyro_energy_pct"]
    .map(k => [k, STAT_META[k].label, STAT_META[k].color, STAT_META[k].icon, "%"]),
];

function EquipBonusList({ bonus }) {
  const rows = EQUIP_BONUS_META.filter(([k]) => bonus?.[k]);
  if (!rows.length) return <div data-testid="equip-bonus-empty" className="text-[11px] font-mono text-[#A89BCC] px-1">No bonuses from gear or food yet. Equip items to power up!</div>;
  return (
    <div className="flex flex-col gap-1.5" data-testid="equip-bonus-list">
      {rows.map(([k, label, color, Icon, suffix]) => (
        <AccompItem key={k} icon={Icon} color={color} label={label} value={`+${Number(bonus[k]).toLocaleString(undefined, { maximumFractionDigits: 1 })}${suffix}`} testId={`equip-bonus-${k}`} />
      ))}
    </div>
  );
}

function InventoryModal({ open, onClose, inventory, items, slot, slotIndex, onEquip, currentItemId, ownFood = [] }) {
  if (!open) return null;
  const filtered = inventory.filter(i => (slot === "food" ? i.slot === "food" : i.slot === slot));
  return (
    <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-md flex items-center justify-center p-4" data-testid="inventory-modal">
      <div className="card-surface p-6 max-w-3xl w-full max-h-[85vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-4">
          <h2 className="font-heading text-2xl neon-cyan uppercase">Equip {slot}{slot==='food' && ` #${slotIndex+1}`}</h2>
          <button onClick={onClose} data-testid="inventory-close" className="text-[#A89BCC] hover:text-[#FF007F]"><X /></button>
        </div>
        {filtered.length === 0 ? (
          <div className="text-center py-12 text-[#A89BCC] font-mono">No matching items in inventory. Buy some at the Shop or beat the right opponent!</div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            {filtered.map((it) => {
              const wearer = it.equipped_by;
              const isCurrent = it.item_id === currentItemId;
              const worn = slot !== "food" && !!wearer && !isCurrent;
              const ownIdx = slot === "food" && !isCurrent ? ownFood.indexOf(it.item_id) : -1;
              const level = it.level || 1;
              const lvlColor = level >= 3 ? "#FFD700" : level >= 2 ? "#39FF14" : "#00F0FF";
              return (
                <div
                  key={it.item_id}
                  data-testid={`inv-item-${it.item_id}`}
                  onClick={() => onEquip(it.item_id)}
                  className={`card-surface p-3 cursor-pointer hover:scale-105 transition-transform relative border-2 ${isCurrent ? "border-[#00F0FF]" : worn ? "border-[#FFE600]/60" : "border-transparent"}`}
                >
                  {isCurrent && (
                    <div className="absolute top-1 right-1 text-[9px] font-heading uppercase neon-cyan tracking-widest z-10">Equipped</div>
                  )}
                  {ownIdx >= 0 && (
                    <div data-testid={`own-food-${it.item_id}`} className="absolute top-1 right-1 text-[9px] font-heading uppercase tracking-widest text-[#FF9500] z-10" title="Already in another food slot — click to move it here">
                      In slot #{ownIdx + 1}
                    </div>
                  )}
                  {worn && (
                    <div data-testid={`worn-by-${it.item_id}`} className="absolute top-1 right-1 text-[9px] font-heading uppercase tracking-widest text-[#FFE600] z-10" title={`Worn by ${wearer.name}`}>
                      by {wearer.name}
                    </div>
                  )}
                  <div className="aspect-square rounded bg-[#0A051A] mb-2 overflow-hidden border border-[#39194D]">
                    <img
                      src={it.image || `/items/${it.item_id}.webp`}
                      alt={it.name}
                      data-testid={`inv-item-img-${it.item_id}`}
                      className="w-full h-full object-cover"
                      onError={(e) => { e.currentTarget.style.display = "none"; }}
                    />
                  </div>
                  <div className="font-heading text-sm mb-1 truncate">{it.name}</div>
                  <div className="flex items-center gap-2 mb-1">
                    <span className="px-1.5 py-0.5 rounded text-[9px] font-heading font-bold" style={{ color: lvlColor, border: `1px solid ${lvlColor}88`, background: "#0A051A" }}>L{level}</span>
                    <span className="text-[10px] font-mono text-[#A89BCC]">{it.next_at ? `${it.copies}/${it.next_at}` : `${it.copies} · MAX`}</span>
                    {it.drop_only && <span className="text-[9px] font-mono text-[#9D00FF] uppercase">Drop</span>}
                  </div>
                  <div className="text-xs font-mono">
                    {Object.entries(it.bonus_pretty || it.bonus || {}).map(([k, v]) => (
                      <div key={k} className="text-[#39FF14]">{fmtBonus(k, v)}</div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

export default function WrestlerPage() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user, refresh } = useAuth();
  const [rosterTut, setRosterTut] = useState(false);
  const [w, setW] = useState(null);
  const [shopItems, setShopItems] = useState([]);
  const [inventory, setInventory] = useState([]);
  const [invOpen, setInvOpen] = useState(false);
  const [invSlot, setInvSlot] = useState(null);
  const [invSlotIdx, setInvSlotIdx] = useState(0);
  const [claimOpen, setClaimOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [floats, setFloats] = useState([]);
  const floatIdRef = useRef(0);
  const fileInputRef = useRef(null);

  const pushFloats = (chips) => {
    if (!chips.length) return;
    // Monotonic ids: guaranteed unique, stable, no collision risk.
    // Stagger with animation-delay so all chips don't hit the same frame.
    const stamped = chips.map((c, idx) => ({
      ...c,
      id: `float-${++floatIdRef.current}`,
      delayMs: idx * 60,
    }));
    setFloats((cur) => [...cur, ...stamped]);
    const lastDelay = stamped[stamped.length - 1].delayMs;
    setTimeout(() => {
      setFloats((cur) => cur.filter((f) => !stamped.find((s) => s.id === f.id)));
    }, 2600 + lastDelay);
  };

  const load = async (autoClaim = false) => {
    const [wr, shop, inv] = await Promise.all([
      api.get(`/wrestlers/${id}`),
      api.get("/shop/items"),
      api.get("/inventory"),
    ]);
    setW(wr.data);
    setShopItems(Array.isArray(shop.data) ? shop.data : (shop.data?.items || []));
    setInventory(inv.data);
    // Auto-open claim popup when this wrestler has passive rewards pending
    if (autoClaim && wr.data?.passive_pending) setClaimOpen(true);
  };

  useEffect(() => { load(true); }, [id]);

  const portraitRef = useRef(null);
  const prevLevel = useRef(null);
  const [lvlFx, setLvlFx] = useState(null);
  useEffect(() => {
    if (!w) return;
    const prev = prevLevel.current;
    prevLevel.current = w.level;
    if (prev == null || w.level <= prev || prev === undefined) return;
    setLvlFx({ key: Date.now(), level: w.level });
    const t = setTimeout(() => setLvlFx(null), 2600);
    return () => clearTimeout(t);
  }, [w?.level]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { prevLevel.current = null; }, [id]);

  const uploadAvatar = async (file) => {
    if (!file) return;
    if (file.size > 3 * 1024 * 1024) {
      toast.error("File must be under 3 MB");
      return;
    }
    if (!file.type.startsWith("image/")) {
      toast.error("Please pick an image file");
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post(`/wrestlers/${w.wrestler_id}/avatar`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      await load();
      toast.success("Avatar updated");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const claimRewards = async () => {
    if (!w) return;
    const endpoint = w.passive_type === "promo"
      ? `/wrestlers/${id}/promo/claim`
      : w.passive_type === "pyro"
      ? `/wrestlers/${id}/pyro/claim`
      : w.passive_type === "tour"
      ? `/wrestlers/${id}/tour/claim`
      : `/wrestlers/${id}/training/claim`;
    // Snapshot for delta feedback
    const before = {
      level: w.level, xp: w.xp,
      power: w.stats?.power || 0,
      workrate: w.stats?.workrate || 0,
      fortune: w.stats?.fortune || 0,
      pop: w.stats?.pop || 0,
    };
    let rainP = Promise.resolve();
    try {
      const r = await api.post(endpoint, { confirm: true });
      if (r.data?.applied) {
        const chips = [];
        if (w.passive_type === "tour") {
          Object.entries(r.data.gained || {}).forEach(([c, v]) => {
            if (Number(v) > 0) chips.push({ type: `tour-${c}`, text: c === "handshakes" ? `+${v} 🤝` : c === "titleshots" ? `+${v} Titleshot` : `+${fmtFans(v)} ${TOUR_LABELS[c]}`, color: "#4D9FFF", anchor: "[data-testid='portrait-level']" });
          });
        } else if (w.passive_type === "promo") {
          const fans = r.data.fans_gained || 0;
          if (fans > 0) chips.push({ type: "fans", text: `+${fans.toLocaleString()} FANS`, color: "#39FF14", anchor: "[data-testid='currency-fans']" });
        } else if (w.passive_type === "pyro") {
          const energy = r.data.energy_gained || 0;
          if (energy > 0) chips.push({ type: "energy", text: `+${energy.toFixed(2)} ⚡`, color: "#FF9500", big: true, anchor: "[data-testid='portrait-level']" });
          if (r.data.doubled) {
            chips.push({ type: "energy-double", text: `⌚ IRON MAN DOUBLE!`, color: "#FFD700", big: true, anchor: "[data-testid='portrait-level']" });
            toast.success("⌚ IRON MAN STOPWATCH TRIGGERED!", {
              description: `Energy claim DOUBLED → +${energy.toFixed(2)} ⚡`,
              duration: 5000,
              style: { background: "linear-gradient(135deg,#2A1944,#FFD70033)", border: "2px solid #FFD700", color: "#FFFFFF" },
            });
          }
        } else {
          const g = r.data.granted || {};
          if (g.hot_dogs) chips.push({ type: "hotdog", text: `+${g.hot_dogs.toLocaleString()}`, color: "#FF007F", anchor: "[data-testid='currency-hotdogs']" });
          if (g.xp)       chips.push({ type: "xp",     text: `+${g.xp.toLocaleString()} XP`,     color: "#00F0FF", anchor: "[data-testid='xp-text']" });
          if (g.kills)    chips.push({ type: "kills",  text: `+${g.kills.toLocaleString()} ☠`,    color: "#FFE600", anchor: "[data-testid='total-kills']" });
          if (r.data.leveled_up) {
            for (let i = 0; i < r.data.leveled_up; i++) {
              chips.push({ type: "level", text: `LEVEL UP!`, color: "#FFD700", big: true, anchor: "[data-testid='portrait-level']" });
            }
          }
          const after = r.data.wrestler?.stats || {};
          const deltas = [
            ["Power",    after.power    - before.power,    "#FF007F", "[data-testid='stat-power']"],
            ["Workrate", after.workrate - before.workrate, "#00F0FF", "[data-testid='stat-workrate']"],
            ["Fortune",  after.fortune  - before.fortune,  "#FFD700", "[data-testid='stat-fortune']"],
          ];
          deltas.forEach(([label, d, color, anchor]) => {
            if (d > 0) chips.push({ type: `stat-${label.toLowerCase()}`, text: `+${d} ${label.toUpperCase()}`, color, anchor });
          });
        }
        const rainFrom = w.passive_type === "training" && r.data.granted?.hot_dogs > 0
          ? document.querySelector("[data-testid='claim-row-hotdogs']")?.getBoundingClientRect() : null;
        setClaimOpen(false);
        if (rainFrom) rainP = hotDogRain(rainFrom, { count: rainCount(r.data.granted.hot_dogs) });
        pushFloats(chips);
      }
      setClaimOpen(false);
      await load();
      await rainP;
      await refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  };

  const openInv = (slot, idx = 0) => { setInvSlot(slot); setInvSlotIdx(idx); setInvOpen(true); };

  const equip = async (item_id) => {
    try {
      await api.post(`/wrestlers/${id}/equip`, { item_id, slot_index: invSlotIdx });
      toast.success("Equipped!");
      setInvOpen(false);
      if (item_id === "face_mask" && ["mask", "done"].includes(user?.train_tut)) setRosterTut(true);
      load();
      refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  };

  const unequip = async (slot, slotIdx = 0) => {
    try {
      await api.post(`/wrestlers/${id}/unequip`, { slot, slot_index: slotIdx });
      load();
      refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  };

  if (!w) return <div className="p-8 text-center font-heading neon-cyan">LOADING...</div>;
  const xpPct = Math.min(100, (w.xp / xpForLevel(w.level)) * 100);

  const tourAssign = async () => {
    try {
      const r = await api.post(`/tour/assign/${id}`);
      if (w.state !== "tour") toast.success(`${w.name} is heading out on the World Tour!`);
      nav(`/tour`, { state: { claimed: r.data.claimed || {}, fromWrestler: id } });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  };
  const pyroAssign = async () => {
    try {
      await api.post(`/pyro/assign/${id}`);
      toast.success(`${w.name} is heading to Pyro!`);
      nav(`/pyro`, { state: { fromWrestler: id } });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  };
  const tileW = user?.pyro_unlocked ? "w-[calc(33.333%-0.42rem)]" : "w-[calc(50%-0.32rem)]";
  const elig = eligibleTitleDivisions(w.class_id);
  const titlesTotal = elig.reduce((sum, div) => sum + (w.titles_won?.[div] || 0), 0);

  return (
    <div className="relative max-w-6xl mx-auto px-4 py-4" data-testid="wrestler-page">
      {lvlFx && <LevelUpSpotlight key={lvlFx.key} fx={lvlFx} anchorRef={portraitRef} />}
      <div className="pointer-events-none absolute inset-0 -z-0 overflow-hidden" aria-hidden="true">
        <div className="wr-spot absolute -top-32 left-[8%] w-[520px] h-[520px] rounded-full blur-3xl" style={{ background: "radial-gradient(circle, rgba(255,0,127,0.22), transparent 65%)" }} />
        <div className="wr-spot absolute top-40 right-[5%] w-[480px] h-[480px] rounded-full blur-3xl" style={{ background: "radial-gradient(circle, rgba(0,240,255,0.16), transparent 65%)", animationDelay: "2.5s" }} />
        <div className="wr-grain absolute inset-0" />
      </div>

      <button data-testid="back-roster" onClick={() => nav("/roster")} className="relative group flex items-center gap-2 text-[#A89BCC] hover:text-[#00F0FF] mb-3 font-mono text-sm uppercase tracking-widest transition-colors">
        <ArrowLeft size={16} className="transition-transform group-hover:-translate-x-1" /> Back to Roster
      </button>

      <div className="relative grid grid-cols-1 lg:grid-cols-3 gap-5">
        {/* Portrait */}
        <GradCard delay={0} testId="wrestler-portrait-card" bg="/backgroundgraphics/Wrestlerpage_Background.webp">
          <div ref={portraitRef} className={`relative rounded-xl p-[3px] overflow-hidden mb-3 mx-auto w-full max-w-[min(360px,45vh)] lg:max-w-none shadow-[0_0_30px_rgba(91,59,255,0.4)] ${lvlFx ? "wr-lvl-frame" : ""}`} data-levelup={lvlFx ? "true" : "false"}>
            <div className="wr-frame-spin absolute -inset-1/2" aria-hidden="true" />
            <div className="relative aspect-square rounded-[10px] overflow-hidden bg-[#0A051A]">
              <WrestlerPortrait w={w} />
              <div className="absolute inset-x-0 bottom-0 h-1/3 bg-gradient-to-t from-[#0A051A]/80 to-transparent pointer-events-none" />
              <div className="wr-portrait-shimmer absolute inset-0 pointer-events-none" aria-hidden="true" />
              {lvlFx && <div key={lvlFx.key} className="wr-lvl-flare absolute inset-0 pointer-events-none z-10" data-testid="levelup-flare" aria-hidden="true" />}
              <div data-testid="portrait-level" className="absolute top-3 left-3 flex flex-col items-center px-3 pt-1 pb-1.5 rounded-lg bg-black/40 backdrop-blur-sm border border-white/15 shadow-[0_0_16px_rgba(0,240,255,0.25)]">
                <Crown size={18} strokeWidth={2.4} className={`${lvlFx ? "wr-crown-spin" : "wr-crown"} text-[#FFD700]`} fill="#FFD70055" style={{ filter: "drop-shadow(0 0 5px #FFD700)" }} />
                <span className="font-num text-2xl leading-none tracking-wider neon-cyan">LV {w.level}</span>
              </div>
              <AvatarHintTooltip side="left" className="absolute bottom-3 right-3">
                <button
                  data-testid="upload-avatar-btn"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploading}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-black/70 hover:bg-black/95 border border-[#00F0FF]/60 hover:border-[#00F0FF] hover:shadow-[0_0_12px_#00F0FF88] font-mono text-[10px] uppercase tracking-widest text-[#00F0FF] hover:text-white transition-[color,border-color,box-shadow,background-color] disabled:opacity-50"
                >
                  <Pencil size={12} strokeWidth={2.6} />
                  {uploading ? "UPLOADING..." : (w.custom_avatar ? "CHANGE AVATAR" : "UPLOAD AVATAR")}
                </button>
              </AvatarHintTooltip>
              <input
                ref={fileInputRef}
                data-testid="avatar-file-input"
                type="file"
                accept="image/png,image/jpeg,image/webp"
                className="hidden"
                onChange={(e) => uploadAvatar(e.target.files?.[0])}
              />
            </div>
          </div>
          <h1 data-testid="wrestler-name" className="font-graffiti text-3xl leading-tight mb-1.5 text-white break-words" style={{ textShadow: "0 0 18px #FF007F99, 3px 3px 0 #000" }}>{w.name}</h1>
          {w.class_info && (
            <div
              data-testid="class-badge"
              className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md mb-2 group relative border cursor-help"
              style={{
                background: w.class_info.color + "22",
                borderColor: w.class_info.color + "88",
                color: w.class_info.color,
                boxShadow: `0 0 12px ${w.class_info.color}33`,
              }}
            >
              <ClassIcon classId={w.class_info.id} color={w.class_info.color} size={16} testId="wrestler-class-icon" />
              <span className="font-heading text-[11px] uppercase tracking-widest">{w.class_info.name}</span>
              <div
                data-testid="class-tooltip"
                className="absolute left-0 top-full mt-1 z-50 hidden group-hover:block bg-[#0A051A] border rounded-lg p-2 min-w-[220px] shadow-lg"
                style={{ borderColor: w.class_info.color + "AA" }}
              >
                <div className="font-heading text-xs mb-1" style={{ color: w.class_info.color }}>{w.class_info.name}</div>
                <div className="text-[11px] font-mono text-[#E5DCFF]">{w.class_info.desc}</div>
              </div>
            </div>
          )}
          <div className="text-xs font-mono text-[#A89BCC] mb-1">
            <span data-testid="xp-text">{w.xp} / {xpForLevel(w.level)} XP</span> · <span data-testid="total-kills" className="text-[#FF3399]">☠ {(w.total_training_kills || 0).toLocaleString()}</span>
          </div>
          <div className="grid grid-cols-3 gap-1.5 mb-2.5 font-mono" data-testid="wrestler-bonuses">
            {[
              ["xp-bonus-text", "XP Bonus", `${w.stats?.xp_bonus || 0}%`, "#39FF14", "left",
               "Extra XP from Training takedowns. Every win brings you closer to Wrestling Space Mountain!"],
              ["passive-gains-text", "Passive Gains", `${w.effective_passive_pct ?? w.promo_passive_pct ?? 25}%`, "#00F0FF", "center",
               "How much this wrestler earns while you're away from the ring, working Training, Promo, Pyro, and Tour!"],
              ["underdog-bonus-text", "Underdog Bonus", `+${w.stats?.underdog_bonus || 0}%`, "#FFE600", "right",
               "Reduces stronger opponent’s win-chance advantage in PvP."],
            ].map(([tid, label, val, color, align, tip]) => (
              <HintTip key={tid} text={tip} color={color} align={align} className="block">
                <div data-testid={tid} className="rounded-md px-2 py-1 border bg-[#0A051A]/70 transition-[border-color,box-shadow] duration-200 hover:shadow-[0_0_10px_var(--c)]"
                     style={{ borderColor: `${color}55`, color, "--c": `${color}66` }}>
                  <div className="text-[9px] uppercase leading-tight text-[#A89BCC]">{label}:</div>
                  <div className="font-num text-base leading-none tracking-wider">{val}</div>
                </div>
              </HintTip>
            ))}
          </div>
          <div className="xp-bar mb-3">
            <div className="xp-fill relative overflow-hidden" style={{ width: `${xpPct}%` }} data-testid="xp-fill"><span className="wr-xp-shine absolute inset-y-0 w-1/3" /></div>
          </div>
          <div className="grid grid-cols-2 gap-2 font-mono text-xs mb-4">
            <div className="rounded-lg p-2 border-2 border-[#39FF14]/40 bg-gradient-to-br from-[#39FF14]/15 to-[#0A051A] flex items-center gap-2" data-testid="pvp-wins-box">
              <Swords size={20} strokeWidth={2.6} className="text-[#39FF14] shrink-0" style={{ filter: "drop-shadow(0 0 5px #39FF14)" }} />
              <div>
                <div className="text-[#A89BCC] text-[10px] uppercase">PvP Wins</div>
                <CountUp value={w.pvp_wins} testId="pvp-wins-value" className="block font-num text-[26px] leading-none tracking-wider text-[#39FF14]" style={{ textShadow: "0 0 10px #39FF1488" }} />
              </div>
            </div>
            <div className="rounded-lg p-2 border-2 border-[#FF007F]/40 bg-gradient-to-br from-[#FF007F]/15 to-[#0A051A] flex items-center gap-2" data-testid="pvp-losses-box">
              <Skull size={20} strokeWidth={2.6} className="text-[#FF007F] shrink-0" style={{ filter: "drop-shadow(0 0 5px #FF007F)" }} />
              <div>
                <div className="text-[#A89BCC] text-[10px] uppercase">PvP Losses</div>
                <CountUp value={w.pvp_losses} testId="pvp-losses-value" className="block font-num text-[26px] leading-none tracking-wider text-[#FF007F]" style={{ textShadow: "0 0 10px #FF007F88" }} />
              </div>
            </div>
          </div>

          {/* ACCOMPLISHMENTS */}
          <div data-testid="wrestler-accomplishments">
            <SectionHeading icon={Trophy} color="#FFD700" small className="mb-2" testId="accomplishments-heading">Accomplishments</SectionHeading>
            {w.current_title && (
              <div
                data-testid="current-title-banner"
                className="mb-2 relative overflow-hidden rounded-lg border-2 p-3 flex items-center gap-3"
                style={{
                  borderColor: "#FFD700",
                  background: "linear-gradient(135deg, rgba(255,215,0,0.25) 0%, rgba(255,215,0,0.08) 40%, rgba(255,0,127,0.15) 100%)",
                  boxShadow: "0 0 22px rgba(255,215,0,0.55), 0 0 44px rgba(255,215,0,0.25) inset",
                  animation: "pulse 3s ease-in-out infinite",
                }}
              >
                <div className="absolute inset-0 pointer-events-none opacity-40" style={{
                  background: "radial-gradient(circle at 20% 30%, rgba(255,215,0,0.6), transparent 40%), radial-gradient(circle at 80% 70%, rgba(255,215,0,0.4), transparent 40%)",
                }} />
                <img
                  src={`/pvp/title_${w.current_title}.png`}
                  alt={w.current_title}
                  className="relative w-14 h-14 rounded-md object-cover flex-shrink-0"
                  style={{ boxShadow: "0 0 18px #FFD700, 0 0 36px #FFD70099" }}
                />
                <div className="relative min-w-0 flex-1">
                  <div className="text-[10px] font-heading uppercase tracking-[0.3em] text-[#FFE600]" style={{ textShadow: "0 0 8px #FFD70099" }}>REIGNING CHAMPION</div>
                  <div className="font-heading text-lg uppercase tracking-widest text-[#FFD700] leading-tight truncate" style={{ textShadow: "0 0 10px #FFD700, 0 0 20px #FFD70088" }}>
                    {DIVISION_LABEL_FULL[w.current_title]}
                  </div>
                  <div className="text-[9px] font-mono text-[#A89BCC] mt-0.5 uppercase tracking-wider">Title held · {(w.titles_won?.[w.current_title] || 0)}× total wins</div>
                </div>
                <Trophy size={30} className="relative shrink-0 text-[#FFD700]" fill="#FFD70066" style={{ filter: "drop-shadow(0 0 6px #FFD700)" }} />
              </div>
            )}
            <div className="grid grid-cols-2 gap-1.5">
              <AccompItem icon={Landmark} color="#B44DFF" label="HOF" value={w.hof_points || 0} testId="accomp-hof" />
              <AccompItem icon={Crown} color="#FFD700" label="Titles" value={titlesTotal} testId="accomp-titles-total"
                          extra={<span className="text-[#A89BCC] text-[9px] ml-1 normal-case">({elig.map(d => DIVISION_LABEL_SHORT[d]).join("/")})</span>} />
              <AccompItem icon={Users} color="#FFE600" label="Rumbles" value={w.rumble_wins || 0} testId="accomp-rumbles" />
              <AccompItem icon={Briefcase} color="#00F0FF" label="Briefcases" value={w.titleshot_bank_wins || 0} testId="accomp-briefcases" />
              <AccompItem icon={Crown} color="#39FF14" label="Ring King" value={w.top_dog_wins || 0} testId="accomp-top-dog" className="col-span-2" />
            </div>
          </div>
        </GradCard>

        {/* Stats + Actions */}
        <GradCard delay={120} testId="wrestler-stats-card" bg="/backgroundgraphics/Wrestlerpage_Background.webp">
          <SectionHeading icon={BarChart3} color="#FF007F" testId="stats-heading">Stats</SectionHeading>
          <div className="flex flex-col gap-3">
            <StatBlock label="Power" value={w.stats.power} color="#FF007F" icon={BicepsFlexed}
                       hint="Damage dealt per hit in Training and this wrestler’s strength in PvP. More muscles means quicker takedowns and a better shot at victory, brother!" />
            <StatBlock label="Workrate" value={w.stats.workrate} color="#00F0FF" icon={Gauge}
                       hint="Every point boosts training drops by +1%. Higher Workrate also means more Tour Fans. Work harder, stack bigger, draw bigger crowds!" />
            <StatBlock label="Fortune" value={w.stats.fortune} color="#FFD700" icon={Clover}
                       hint="Every point adds +1% drop chance. Because the wrestling gods play favorites! Fortune also increases Pyro Energy gains." />
          </div>

          <SectionHeading icon={Zap} color="#00F0FF" className="mt-7 mb-4" testId="actions-heading">Actions</SectionHeading>
          <div className="flex flex-wrap justify-center gap-2.5" data-testid="actions-grid">
            <ActionTile className={tileW} testId="action-train" color="#FF007F" icon={Dumbbell} label="Train"
                        onClick={() => { if (user?.tutorial_stage !== "wrestler") nav(`/wrestler/${id}/training`); }} />
            {user?.pyro_unlocked && <ActionTile className={tileW} testId="action-pyro" color="#FF9500" icon={Flame} label="Pyro" onClick={pyroAssign} />}
            <ActionTile className={tileW} testId="action-tour" color="#4D9FFF" icon={Globe2} label="Tour" onClick={tourAssign} />
            <ActionTile className={tileW} testId="action-fight" color="#FF3B3B" icon={Swords} label="Fight" onClick={() => nav("/pvp")} />
            {/*<ActionTile className={tileW} testId="action-promo" color="#39FF14" icon={Megaphone} label="Promo" onClick={() => nav(`/wrestler/${id}/promo`)} />*/}
          </div>
        </GradCard>

        {/* Gear */}
        <GradCard delay={240} testId="wrestler-gear-card" bg="/backgroundgraphics/Wrestlerpage_Background2.webp">
          <SectionHeading icon={Shirt} color="#00F0FF" testId="equipment-heading">Equipment</SectionHeading>
          <div className="grid grid-cols-3 gap-3 mb-6">
            <GearSlot slot="face" label="Face" itemId={w.gear.face} items={shopItems} onUnequip={() => unequip("face")} onClick={() => openInv("face")} detail={w.gear_details?.face} />
            <GearSlot slot="jacket" label="Jacket" itemId={w.gear.jacket} items={shopItems} onUnequip={() => unequip("jacket")} onClick={() => openInv("jacket")} detail={w.gear_details?.jacket} />
            <GearSlot slot="boots" label="Boots" itemId={w.gear.boots} items={shopItems} onUnequip={() => unequip("boots")} onClick={() => openInv("boots")} detail={w.gear_details?.boots} />
          </div>

          <SectionHeading icon={UtensilsCrossed} color="#FFE600" small className="mb-4" testId="food-heading">Food Buffs</SectionHeading>
          <div className="grid grid-cols-3 gap-3">
            {Array.from({ length: user?.food_cap || 3 }).map((_, idx) => (
              <GearSlot
                key={idx}
                slot="food"
                label={`Food ${idx+1}`}
                itemId={(w.food || [])[idx]}
                items={shopItems}
                onUnequip={() => unequip("food", idx)}
                onClick={() => openInv("food", idx)}
                detail={(w.food_details || [])[idx] || undefined}
              />
            ))}
          </div>

          <SectionHeading icon={PlusCircle} color="#39FF14" small className="mt-6 mb-3" testId="equip-bonus-heading">Total Bonuses</SectionHeading>
          <EquipBonusList bonus={w.equip_bonus} />
        </GradCard>
      </div>

      <InventoryModal
        open={invOpen}
        onClose={() => setInvOpen(false)}
        inventory={inventory}
        items={shopItems}
        slot={invSlot}
        slotIndex={invSlotIdx}
        onEquip={equip}
        currentItemId={invSlot === "food" ? w.food[invSlotIdx] : w.gear[invSlot]}
        ownFood={w.food || []}
      />

      {/* Blocking passive-rewards claim popup — must be dismissed before any action on this page. */}
      {claimOpen && w.passive_pending && (
        <PassiveClaimModal w={w} onClaim={claimRewards} />
      )}

      {/* Floating stat-gain feedback chips after claim */}
      <FloatFeedback floats={floats} />
      {user?.tutorial_stage === "wrestler" && (
        <RickyTutorial exitAfter
          steps={[
            { text: "OWWWWWWW! I think I just pinched a nerve!" },
            { text: "Here’s where you manage your superstar, rookie! Have Five Handshakes to get you started.", target: "[data-testid='currency-handshakes']",
              onEnter: async () => { try { await api.post("/tutorial/handshakes"); await refresh(); } catch {} } },
            { text: "Better put that superstar to work in Training!", target: "[data-testid='action-train']" },
            { text: "You think you’ve got what it takes to climb Space Mountain, rookie? Prove it" },
            { text: "OWWWW… That hurts, I need a stretcher! Make sure it’s gold-plated!" },
          ]}
          onFinish={async () => { try { await api.post("/tutorial/advance", { stage: "done" }); } catch {} refresh(); }} />
      )}
      {rosterTut && (
        <RickyTutorial exitAfter noBlock
          steps={[
            { text: "Look at you! Time to BUILD THAT ROSTER! Create another wrestler!" },
            { text: "You fight for ACTIVE gains, while your other superstars earn PASSIVE rewards! MORE WRESTLERS, MORE GAINS! OWWWWW, my back!" },
          ]}
          onFinish={async () => { setRosterTut(false); try { await api.post("/tutorial/training", { step: "roster" }); } catch {} refresh(); }} />
      )}
    </div>
  );
}

/* =========================================================
   FLOAT FEEDBACK — animated stat-gain chips anchored to their
   corresponding UI element (portrait level, kills, stat bars,
   currency badges).
   ========================================================= */
function AnchoredFloat({ f }) {
  const [pos, setPos] = React.useState(null);
  const mountedRef = React.useRef(false);
  React.useLayoutEffect(() => {
    // Guard against StrictMode's dev double-invoke — only compute once.
    if (mountedRef.current) return;
    mountedRef.current = true;
    const el = f.anchor ? document.querySelector(f.anchor) : null;
    if (el) {
      const r = el.getBoundingClientRect();
      setPos({ left: r.left + r.width / 2, top: r.top + r.height / 2 });
    } else {
      setPos({ left: window.innerWidth / 2, top: window.innerHeight * 0.3 });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  if (!pos) return null;
  const inner = (
    <div
      data-testid={`claim-float-${f.type}`}
      className="whitespace-nowrap font-heading font-bold uppercase tracking-widest px-3 py-1.5 rounded-lg"
      style={{
        color: f.color,
        background: "#0A051AE6",
        border: `2px solid ${f.color}`,
        boxShadow: `0 0 16px ${f.color}AA`,
        textShadow: `0 0 6px ${f.color}`,
        fontSize: f.big ? "22px" : "14px",
        willChange: "transform, opacity",
        opacity: 0,
        animation: `${f.big ? "rk-float-big" : "rk-float-up"} 2.4s cubic-bezier(0.2, 0.7, 0.3, 1) ${f.delayMs || 0}ms forwards`,
      }}
    >
      {f.big && <ArrowUp size={22} className="inline mr-1 -mt-1" />}
      {typeof f.type === "string" && f.type.startsWith("stat-") && <ArrowUp size={12} className="inline mr-1 -mt-0.5" />}
      {f.type === "hotdog" && <img src={ASSETS.currency.hotdog} alt="" className="inline w-5 h-5 mr-1 -mt-1" />}
      {f.text}
    </div>
  );
  return (
    <div
      className="fixed pointer-events-none"
      style={{ left: pos.left, top: pos.top, transform: "translate(-50%, -50%)", zIndex: 60 }}
    >
      {inner}
    </div>
  );
}

function FloatFeedback({ floats }) {
  if (!floats.length) return null;
  return (
    <>
      <style>{`
        @keyframes rk-float-up {
          0%   { opacity: 0; transform: translateY(20px) scale(0.6); }
          15%  { opacity: 1; transform: translateY(0) scale(1.15); }
          30%  { transform: translateY(-6px) scale(1); }
          80%  { opacity: 1; transform: translateY(-38px) scale(1); }
          100% { opacity: 0; transform: translateY(-70px) scale(0.95); }
        }
        @keyframes rk-float-big {
          0%   { opacity: 0; transform: translateY(30px) scale(0.4) rotate(-8deg); }
          25%  { opacity: 1; transform: translateY(0) scale(1.4) rotate(2deg); }
          40%  { transform: translateY(-8px) scale(1.2) rotate(-2deg); }
          85%  { opacity: 1; transform: translateY(-50px) scale(1.1); }
          100% { opacity: 0; transform: translateY(-100px) scale(1); }
        }
      `}</style>
      {floats.map((f) => <AnchoredFloat key={f.id} f={f} />)}
    </>
  );
}

/* =========================================================
   PASSIVE CLAIM MODAL — vertical, iconography-forward
   ========================================================= */
function ClaimStatRow({ icon, label, value, color, testId }) {
  return (
    <div
      data-testid={testId}
      className="flex items-center gap-3 px-3 py-2.5 rounded-lg bg-[#0A051A] border"
      style={{ borderColor: color + "66" }}
    >
      <div
        className="w-10 h-10 rounded-md flex items-center justify-center shrink-0"
        style={{ background: color + "22", border: `1px solid ${color}88`, boxShadow: `0 0 12px ${color}44` }}
      >
        {icon}
      </div>
      <div className="flex-1">
        <div className="text-[10px] uppercase tracking-widest text-[#A89BCC] font-mono">{label}</div>
      </div>
      <div
        className="font-heading text-2xl leading-none"
        style={{ color, textShadow: `0 0 12px ${color}88` }}
      >
        +{Number(value || 0).toLocaleString()}
      </div>
    </div>
  );
}

function DropThumb({ src, count, color = "#39FF14", fallbackLabel = "?", testId }) {
  const [broken, setBroken] = useState(!src);
  return (
    <div
      data-testid={testId}
      className="relative w-14 h-14 rounded-md overflow-hidden bg-[#0A051A] border shrink-0 flex items-center justify-center"
      style={{ borderColor: color + "88", boxShadow: `0 0 8px ${color}33` }}
    >
      {!broken ? (
        <img
          src={src}
          alt=""
          className="w-full h-full object-cover"
          onError={() => setBroken(true)}
        />
      ) : (
        <span
          className="font-heading text-[10px] uppercase tracking-widest px-1 text-center leading-tight"
          style={{ color, textShadow: `0 0 6px ${color}` }}
        >
          {fallbackLabel}
        </span>
      )}
      {count > 1 && (
        <div
          className="absolute bottom-0 right-0 px-1 font-heading text-[10px] leading-tight font-bold"
          style={{ background: "#0A051AEE", color, textShadow: `0 0 4px ${color}` }}
        >
          ×{count}
        </div>
      )}
    </div>
  );
}

function DropGroup({ label, color, testId, children }) {
  return (
    <div data-testid={testId}>
      <div className="font-heading text-[9px] sm:text-[10px] uppercase tracking-wide sm:tracking-widest mb-1.5 text-center whitespace-nowrap" style={{ color, textShadow: `0 0 6px ${color}88` }}>
        {label}
      </div>
      <div className="flex flex-wrap justify-center gap-2">{children}</div>
    </div>
  );
}

function stageFromCardId(did) {
  // dummy ids "d01_jimmy_jobber" / boss ids "b10_macho_ham" → stage number
  const m = /^[db](\d+)_/.exec(did || "");
  return m ? parseInt(m[1], 10) : null;
}

const CLAIM_THEME = {
  training: { color: "#FF007F", icon: Dumbbell, title: "Training Rewards" },
  tour:     { color: "#4D9FFF", icon: Globe2,   title: "Tour Rewards" },
  pyro:     { color: "#FF9500", icon: Flame,    title: "Pyro Energy" },
  promo:    { color: "#FFE600", icon: Sparkles, title: "Passive Rewards", dark: true },
};

function PassiveClaimModal({ w, onClaim }) {
  const isPromo = w.passive_type === "promo";
  const isPyro = w.passive_type === "pyro";
  const theme = CLAIM_THEME[w.passive_type] || CLAIM_THEME.training;
  const claimColor = theme.color;
  const TitleIcon = theme.icon;
  const s = w.passive_summary || {};
  const cardsEntries = Object.entries(s.cards || {});
  const colEntries   = Object.entries(s.collectibles || {});
  const gearEntries  = Object.entries(s.gear || {});
  const trophies     = s.trophies || [];
  const hasDrops = cardsEntries.length || colEntries.length || gearEntries.length || trophies.length;

  return (
    <div className="fixed inset-0 z-50 bg-black/90 backdrop-blur-md flex items-center justify-center p-4" data-testid="passive-claim-modal">
      <div className="card-surface p-6 max-w-md w-full border-2 max-h-[90vh] overflow-y-auto" style={{ borderColor: claimColor, boxShadow: `0 0 30px ${claimColor}88` }}>
        <div className="flex items-center gap-2 mb-1">
          <TitleIcon size={24} strokeWidth={2.6} data-testid="claim-title-icon" style={{ color: claimColor, filter: `drop-shadow(0 0 6px ${claimColor})` }} />
          <h2 data-testid="claim-title" className="font-heading text-2xl uppercase" style={{ color: claimColor, textShadow: `0 0 10px ${claimColor}` }}>
            {theme.title}
          </h2>
        </div>
        <div className="text-[11px] font-mono text-[#A89BCC] mb-4">
          {isPromo
            ? <>While <span className="neon-pink">{w.name}</span> kept working the crowd…</>
            : isPyro
            ? <>While <span className="neon-pink">{w.name}</span> lit up the Pyro stage…</>
            : w.passive_type === "tour"
            ? <>While <span className="neon-pink">{w.name}</span> was out on World Tour, winning over crowds…</>
            : <>While <span className="neon-pink">{w.name}</span> kept grinding stage {w.training_stage}…</>}
        </div>

        {w.passive_type === "tour" ? (
          <div className="p-4 rounded-lg bg-[#0A051A] border-2 mb-5 flex flex-col gap-2" style={{ borderColor: "#4D9FFFAA", boxShadow: "0 0 24px #4D9FFF44" }} data-testid="claim-tour-section">
            <div className="text-[11px] uppercase tracking-widest text-[#A89BCC] font-mono">World Tour earnings</div>
            {Object.entries(w.tour_pending || {}).filter(([, v]) => Number(v) > 0).map(([c, v]) => (
              <div key={c} className="flex items-center gap-3" data-testid={`claim-tour-${c}`}>
                <Flag c={c} className={c === "handshakes" || c === "titleshots" ? "w-6 h-6" : "w-7 h-5"} />
                <span className="font-mono text-xs text-[#A89BCC] flex-1">{TOUR_LABELS[c]}</span>
                <span className="font-heading text-2xl text-[#4D9FFF]" style={{ textShadow: "0 0 10px #4D9FFF88" }}>+{c === "handshakes" || c === "titleshots" ? v : fmtFans(v)}</span>
              </div>
            ))}
          </div>
        ) : isPyro ? (
          <div className="p-5 rounded-lg bg-[#0A051A] border-2 mb-5 flex items-center gap-4"
               style={{ borderColor: "#FFD700AA", boxShadow: "0 0 24px #FFD70055" }}>
            <Zap size={56} className="text-[#FFD700] shrink-0" fill="#FFD700" style={{ filter: "drop-shadow(0 0 10px #FFD700)" }} />
            <div className="flex-1">
              <div className="text-[11px] uppercase tracking-widest text-[#A89BCC] font-mono">Energy Earned</div>
              <div className="font-heading text-5xl leading-none text-[#FFD700]" style={{ textShadow: "0 0 18px #FFD70088" }} data-testid="claim-pyro-energy">
                +{Number(w.pyro_pending_energy || 0).toFixed(2)}
              </div>
              <div className="text-[10px] font-mono text-[#FF9500] mt-1">
                → shared Pyro Energy pool
              </div>
            </div>
          </div>
        ) : isPromo ? (
          <div className="p-5 rounded-lg bg-[#0A051A] border-2 mb-5 flex items-center gap-4"
               style={{ borderColor: "#39FF14AA", boxShadow: "0 0 24px #39FF1444" }}>
            <img src={ASSETS.currency.fan} alt="Fans" className="w-16 h-16 shrink-0" style={{ filter: "drop-shadow(0 0 8px #39FF14)" }} />
            <div className="flex-1">
              <div className="text-[11px] uppercase tracking-widest text-[#A89BCC] font-mono">Fans Earned</div>
              <div className="font-heading text-5xl leading-none text-[#39FF14]" style={{ textShadow: "0 0 18px #39FF14AA" }} data-testid="claim-promo-fans">
                +{(w.promo_pending_fans || 0).toLocaleString()}
              </div>
            </div>
          </div>
        ) : (
          <>
            <div className="flex flex-col gap-2 mb-4">
              <ClaimStatRow
                testId="claim-row-hotdogs"
                icon={<img src={ASSETS.currency.hotdog} alt="" className="w-7 h-7" />}
                label="Hot Dogs"
                value={s.hot_dogs}
                color="#FF007F"
              />
              <ClaimStatRow
                testId="claim-row-xp"
                icon={<Zap size={20} className="text-[#00F0FF]" style={{ filter: "drop-shadow(0 0 4px #00F0FF)" }} />}
                label="XP Earned"
                value={s.xp}
                color="#00F0FF"
              />
              <ClaimStatRow
                testId="claim-row-kills"
                icon={<Skull size={20} className="text-[#FFE600]" style={{ filter: "drop-shadow(0 0 4px #FFE600)" }} />}
                label="Kills"
                value={s.kills}
                color="#FFE600"
              />
            </div>

            {hasDrops ? (
              <div className="mb-5" data-testid="claim-drops-section">
                <div className="flex items-center gap-2 mb-2">
                  <div className="h-px flex-1 bg-gradient-to-r from-transparent via-[#9D00FF] to-transparent" />
                  <div className="font-heading text-xs uppercase tracking-widest text-[#B39BFF]">Drops</div>
                  <div className="h-px flex-1 bg-gradient-to-r from-transparent via-[#9D00FF] to-transparent" />
                </div>
                <div className="flex flex-wrap justify-center items-start gap-x-2 sm:gap-x-4 gap-y-3 p-3 rounded-lg bg-[#0A051A] border border-[#9D00FF]/40">
                  {trophies.length > 0 && (
                    <DropGroup label="Trophy" color="#FFD700" testId="drop-group-trophy">
                      {trophies.map((tid, i) => (
                        <DropThumb key={`t-${tid}-${i}`} src={`/trophies/${tid}.webp`} count={1} color="#FFD700" fallbackLabel="🏆" testId={`drop-trophy-${tid}`} />
                      ))}
                    </DropGroup>
                  )}
                  {gearEntries.length > 0 && (
                    <DropGroup label="Gear" color="#00F0FF" testId="drop-group-gear">
                      {gearEntries.map(([gid, qty]) => (
                        <DropThumb key={`g-${gid}`} src={`/items/${gid}.webp`} count={qty} color="#00F0FF" fallbackLabel="Gear" testId={`drop-gear-${gid}`} />
                      ))}
                    </DropGroup>
                  )}
                  {colEntries.length > 0 && (
                    <DropGroup label="Collectibles" color="#9D00FF" testId="drop-group-collectibles">
                      {colEntries.map(([cid, qty]) => (
                        <DropThumb key={`c-${cid}`} src={`/collectibles/${cid}.webp`} count={qty} color="#9D00FF" fallbackLabel="Coll." testId={`drop-col-${cid}`} />
                      ))}
                    </DropGroup>
                  )}
                  {cardsEntries.length > 0 && (
                    <DropGroup label="Cards" color="#FF007F" testId="drop-group-cards">
                      {cardsEntries.map(([did, qty]) => {
                        const stage = stageFromCardId(did);
                        return (
                          <DropThumb
                            key={`d-${did}`}
                            src={stage ? `/npcs/${stage}.webp` : ""}
                            count={qty}
                            color="#FF007F"
                            fallbackLabel="Card"
                            testId={`drop-card-${did}`}
                          />
                        );
                      })}
                    </DropGroup>
                  )}
                </div>
              </div>
            ) : (
              <div className="mb-4 text-center text-[11px] font-mono text-[#695D8E]">No drops this session</div>
            )}
          </>
        )}

        {w.passive_type !== "promo" ? (
          <ClaimButton type={w.passive_type || "training"} label="CLAIM" testId="claim-confirm" onClick={onClaim} className="w-full min-h-[56px]" />
        ) : (
        <button
          data-testid="claim-confirm"
          onClick={onClaim}
          className="w-full btn-neon py-3 text-sm flex items-center justify-center gap-2 font-bold"
          style={{ background: `linear-gradient(135deg, ${claimColor}, color-mix(in srgb, ${claimColor} 50%, #0A051A))`, color: theme.dark ? "#0A051A" : "#FFFFFF",
                   boxShadow: `0 0 18px ${claimColor}88`, textShadow: theme.dark ? "none" : "0 1px 2px rgba(0,0,0,0.6)" }}
        >
          <TitleIcon size={16} strokeWidth={2.8} /> CLAIM & CONTINUE
        </button>
        )}
      </div>
    </div>
  );
}
